'use client'

// imports
import { useAtom } from 'jotai';
import { useEffect, useMemo } from 'react';

// lib files
import { handleRecommendedShowClick } from '@/lib/content-services/rec-engine';
import { useHasMounted } from '@/lib/hooks';
import { ProfileData } from '@/lib/types/api/profile-data';
import { recommendedShowsAtom, getGenreRecommendedShowsAtom } from '@/lib/atoms/recommended-shows';
import { sendPbsGtmEvent } from "@/lib/helpers/send-pbs-gtm-event";
import { ShowRowContent } from '@/lib/types/api/show-data';

// types
import type { ShowBadgeFlags } from '@/components/ShowPoster/ShowPoster';

// constants
import { RECOMMENDED_SHOWS_FEATURE_CATEGORY } from "@/lib/constants";

// components
import FeaturedShowsRow from '@/app/(main)/HomePage/FeaturedShowsRow';
import ShowRow from "@/components/ShowRow/ShowRow";
import { useIdentity } from '@/lib/hooks/useIdentity';


interface TopPicksShowRowProps {
  profileStub?: ProfileData;
  depTopPicksShowData?: ShowRowContent[];
  featuredShows?: ShowRowContent[];
  showBadgeFlags?: ShowBadgeFlags;
  genre?: string;
}

const TopPicksShowRow = (props: TopPicksShowRowProps) => {
  const { profileStub, depTopPicksShowData, featuredShows, showBadgeFlags, genre } = props;
  const hasMounted = useHasMounted();
  const { isAuthenticated, cachedIdentity } = useIdentity()

  const userFirstName = profileStub ? profileStub.profile.first_name : cachedIdentity?.firstName;
  const atom = useMemo(
    () => genre ? getGenreRecommendedShowsAtom(genre) : recommendedShowsAtom,
    [genre]
  );
  const [topPicksData, setTopPicksData] = useAtom(atom);

  useEffect(() => {
    const fetchTopPicksData = async () => {
      try {
        const params = new URLSearchParams();
        if (genre) params.append('genre', genre);
        const query = params.toString();
        const response = await fetch(`/api/profile/recommended-shows/${query ? `?${query}` : ''}`)
        const json = await response.json();
        const topPicksResponse = json.recommendedShowsData;
        setTopPicksData(topPicksResponse);
      } catch (error) {
        console.error(error);
      }
    }

    if (depTopPicksShowData) {
      setTopPicksData(depTopPicksShowData)
    } else {
      if (isAuthenticated) {
        fetchTopPicksData()
      }
    }
  }, [isAuthenticated, depTopPicksShowData, setTopPicksData, genre]);

  const handleRecommendedShowPosterClick = (show: ShowRowContent, index?: number) => {
    handleRecommendedShowClick(show);
    sendPbsGtmEvent({
      feature_category: RECOMMENDED_SHOWS_FEATURE_CATEGORY,
      feature_name: "recommended show poster",
      object_action_behavior: "user navigates to show page",
      object_action: "click",
      object_location: genre ? "explore topic recommended show row" : "homepage recommended show row",
      object_name: "show poster",
      // This check is rather elaborate because 0 is considered falsey
      object_position: index !== undefined ? index + 1 : undefined,
      object_type: "link",
      object_url: `/show/${show.slug}/`,
      pbs_content_id: show.slug,
      show_title: show.title,
      signed_in_status: true,
    });
  }

  if (!hasMounted) {
    return null;
  }

  // Falls back to display the featured shows if the top picks data is not available
  return (
    <>
      { (topPicksData && Array.isArray(topPicksData) && topPicksData.length > 0) ? (
        <ShowRow
          shows={topPicksData}
          title={`Top Picks for ${userFirstName ? userFirstName : "You"}`}
          onShowPosterClick={handleRecommendedShowPosterClick}
          showBadgeFlags={showBadgeFlags}
        />
      ) : (
        featuredShows && <FeaturedShowsRow shows={featuredShows} showBadgeFlags={showBadgeFlags} />
      )}
    </>
  )
}

export default TopPicksShowRow;
